import axios from 'axios';
import { buildBiosdkMinexRouteUrl } from './biosdkRoutes';

export const MINEX_FINGER_POSITION = {
  UNKNOWN: 0,
  RIGHT_THUMB: 1,
  RIGHT_INDEX_FINGER: 2,
  RIGHT_MIDDLE_FINGER: 3,
  RIGHT_RING_FINGER: 4,
  RIGHT_LITTLE_FINGER: 5,
  LEFT_THUMB: 6,
  LEFT_INDEX_FINGER: 7,
  LEFT_MIDDLE_FINGER: 8,
  LEFT_RING_FINGER: 9,
  LEFT_LITTLE_FINGER: 10,
};

export function toSignedByteJson(arrayBuffer) {
  const unsigned = new Uint8Array(arrayBuffer);
  return Array.from(unsigned, (v) => (v > 127 ? v - 256 : v));
}

export function minexFingerPositionFromOption(selectedOption) {
  const subType = String(selectedOption || '').split('|')[0].trim();
  return MINEX_FINGER_POSITION[subType] ?? MINEX_FINGER_POSITION.UNKNOWN;
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to decode image'));
    };
    img.src = url;
  });
}

/**
 * Decode a plain image file to grayscale signed-byte pixels for MINEX extract.
 */
export async function fileToGrayscalePixels(file) {
  const img = await loadImageFromFile(file);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not available');
  ctx.drawImage(img, 0, 0);
  const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = [];
  for (let i = 0; i < data.length; i += 4) {
    const gray = Math.round(data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114);
    pixels.push(gray > 127 ? gray - 256 : gray);
  }
  return { width, height, pixels };
}

export async function readAnsiTemplateBytes(file) {
  const buf = await file.arrayBuffer();
  return toSignedByteJson(buf);
}

export async function extractMinexFingerTemplate(
  biosdkBaseUrl,
  file,
  { fingerPosition = 0, impressionType = 0, quality = 60, dpi = 500 } = {}
) {
  const ext = file.name.split('.').pop().toLowerCase();
  if (ext === 'bin') {
    const template = await readAnsiTemplateBytes(file);
    return { template, templateLength: template.length, format: 'ANSI_INCITS_378_2004' };
  }

  const { width, height, pixels } = await fileToGrayscalePixels(file);
  const url = buildBiosdkMinexRouteUrl(biosdkBaseUrl, 'extract-template');
  const body = {
    width,
    height,
    dpi,
    fingerPosition,
    impressionType,
    quality,
    image: pixels,
  };
  const response = await axios.post(url, body, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}

export async function matchMinexFingerTemplates(biosdkBaseUrl, verificationTemplate, enrollmentTemplate) {
  const url = buildBiosdkMinexRouteUrl(biosdkBaseUrl, 'match');
  const body = {
    verificationTemplate,
    enrollmentTemplate,
  };
  const response = await axios.post(url, body, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}

export async function resolveMinexTemplateFromFile(biosdkBaseUrl, file, fingerOption) {
  const ext = file.name.split('.').pop().toLowerCase();
  if (ext === 'bin') {
    return readAnsiTemplateBytes(file);
  }
  const fingerPosition = minexFingerPositionFromOption(fingerOption);
  const result = await extractMinexFingerTemplate(biosdkBaseUrl, file, { fingerPosition });
  return result.template;
}
